import SubmittalMaterialView from "components/gantt-layout/submittal-material-view";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import "components/gantt-layout/style.scss";
import { formatDate } from "pages/schedule/schedule-page/procurment-schedule/procurment-schedule";
import { Spin } from "antd";

interface SubTask {
  id: string;
  text: string;
  name_milestone: string;
  start_date: string | null;
  end_date: string | null;
  parent: string;
  type: string;
  risk_level: string;
  risk_assessment: string;
  current_step?: boolean;
  hoverText: {
    text: string;
    start_date: string | null;
    end_date: string | null;
    risk_level: string;
    risk_assessment: string;
  };
}

interface Task {
  risk_assessment?: any;
  id: string;
  text: string;
  start_date: string | null;
  end_date?: string | null;
  parent?: string;
  type?: string;
  risk_level?: string;
  activity_count?: number;
  open?: boolean;
  render?: string;
  milestones?: SubTask[];
  workflow_status?: string;
  progress?: any;
  actual_float?: number;
  submittal_actual_float?: number;
  material_actual_float?: number;
  material_schedule_links?: Array<any>;
}

interface Link {
  id: string;
  source: string;
  target: string;
  type: any;
}

interface GanttData {
  data: Task[];
  links: Link[];
}

interface ResponseObject {
  vendors: Task[];
  submittals: Task[];
  materials: Task[];
  links: Link[];
}

function ProjectTimeline({ zoomSelection }: { zoomSelection: string }) {
  const { projectId } = useParams() as any;

  const [ganttTasks, setTasks] = useState<GanttData>({ data: [], links: [] });

  const [isOpen] = useState<boolean>(false);
  const togglePanel = (): void => {
    // setIsOpen(!isOpen);
  };

  const createGanttData = (respObj: ResponseObject): void => {
    const data: Task[] = [];
    // let index: number = 0;
    const uniqueIds = new Set<string>();

    const { vendors } = respObj;
    const submittals = respObj.submittals.sort((a, b) =>
      a.text.localeCompare(b.text)
    );
    const materials = respObj.materials.sort((a, b) =>
      a.text.localeCompare(b.text)
    );
    const { links } = respObj;

    const childMap = new Map<string, string[]>();
    links.forEach((link) => {
      const sourceId = link.source;
      const targetId = link.target;

      if (!childMap.has(sourceId)) {
        childMap.set(sourceId, []);
      }
      childMap.get(sourceId)!.push(targetId);
    });

    vendors.forEach((vendor) => {
      let leastStartDate = new Date("9999-12-31");

      const vendorSubmittals = submittals.filter(
        (submittal) => submittal.parent === vendor.id
      );

      vendorSubmittals.forEach((submittal) => {
        const submittalStartDate = new Date(submittal.start_date!);
        if (submittal.start_date && submittalStartDate < leastStartDate) {
          leastStartDate = submittalStartDate;
        }

        if (!uniqueIds.has(submittal.id)) {
          uniqueIds.add(submittal.id);
          const submittalNode: Task = {
            id: submittal.id,
            text: submittal.text,
            start_date: formatDate(submittal.start_date),
            end_date: formatDate(submittal.end_date),
            parent: vendor.id,
            type: submittal.type,
            risk_level: submittal.risk_level,
            risk_assessment: submittal.risk_assessment,
            render: "split",
            open: false,
            workflow_status: submittal.workflow_status,
            progress:
              submittal.workflow_status === "IN_PROGRESS" ? "0.5" : undefined,
            actual_float: submittal.submittal_actual_float
          };
          data.push(submittalNode);
        }

        const linkedMaterialIds = childMap.get(submittal.id) || [];
        linkedMaterialIds.forEach((materialId) => {
          const material = materials.find((mat) => mat.id === materialId);

          const materialStartDate = new Date(material?.start_date!);
          if (material?.start_date && materialStartDate < leastStartDate) {
            leastStartDate = materialStartDate;
          }

          if (material && !uniqueIds.has(material.id)) {
            uniqueIds.add(material.id);
            const materialNode: Task = {
              id: material.id,
              text: material.text,
              start_date: formatDate(material.start_date),
              end_date: formatDate(material.end_date),
              parent: vendor.id,
              type: material.type,
              risk_level: material.risk_level,
              risk_assessment: material.risk_assessment,
              activity_count: material.activity_count,
              render: "split",
              open: false,
              workflow_status: material.workflow_status,
              progress:
                material.workflow_status === "IN_PROGRESS" ? "0.5" : undefined,
              actual_float: material.material_actual_float,
              material_schedule_links: material.material_schedule_links
            };
            data.push(materialNode);
          }
        });
      });

      const vendorStartDate =
        leastStartDate < new Date("9999-12-31")
          ? formatDate(leastStartDate.toISOString())
          : null;
      const vendorNode: Task = {
        id: vendor.id,
        text: vendor.text,
        start_date: vendorStartDate,
        type: vendor.type,
        risk_level: ""
      };
      data.push(vendorNode);
    });

    const tasks: GanttData = {
      data,
      links: respObj.links
    };
    setTasks(tasks);
  };

  useEffect(() => {
    // const url = window.location.href;
    // const projectId = url.split("project/")[1].split("/")[0];
    // fetch(`http://54.227.202.17:4000/procurement-schedule/${projectId}`)
    fetch(
      `https://proc-schedule.constructiviq.com/procurement-schedule/${projectId}`
    )
      .then((response) => response.json())
      .then((data: ResponseObject) => createGanttData(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, [projectId]);

  return (
    <div className="flex h-full w-full justify-center items-center">
      {ganttTasks.data.length > 0 ? (
        <SubmittalMaterialView
          task={ganttTasks}
          zoomSelection={zoomSelection}
          togglePanel={togglePanel}
          isOpen={isOpen}
        />
      ) : (
        <Spin size="large" />
      )}
    </div>
  );
}

export default ProjectTimeline;
